/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { createApiClient } from "@/lib/api/api-client";
import { logger } from "@/lib/utils/logger";
import type {
  OrganizationData,
  OrganizationSettings,
  OrganizationStats,
} from "@/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchOrganizationApi,
  fetchStatsApi,
  updateOrganizationApi,
  updateSettingsApi,
  validateSlugApi,
  uploadLogoApi,
} from "./api";
import { runDeduplicated } from "./in-flight-cache";
import type { OrganizationDataContextValue } from "./types";

const CACHE_KEY_FETCH = "organization:fetch";
const CACHE_KEY_STATS = "organization:stats";

function isNetworkOrAuthError(err: Error): boolean {
  if (err.message.includes("401")) return true;
  if (
    err.message.includes("Failed to fetch") ||
    err.message.includes("NetworkError") ||
    err.message.includes("network")
  ) {
    return true;
  }
  return false;
}

export function useOrganizationDataState(): OrganizationDataContextValue {
  const { isSignedIn, isLoaded, orgId, getToken } = useAuthSafe();
  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => orgId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, orgId],
  );
  const [organization, setOrganization] = useState<OrganizationData | null>(
    null,
  );
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasFetchedRef = useRef(false);
  const lastAuthStateRef = useRef<{
    isLoaded: boolean;
    isSignedIn: boolean | undefined;
    orgId: string | null;
  } | null>(null);

  const fetchOrganization = useCallback(async () => {
    if (!isLoaded || !isSignedIn || !orgId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await runDeduplicated(CACHE_KEY_FETCH, () =>
        fetchOrganizationApi(api),
      );
      if (data != null) setOrganization(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      if (err instanceof Error && isNetworkOrAuthError(err)) {
        logger.debug(
          "Organization fetch network error (expected on unauthenticated pages)",
          { error: message },
        );
        return;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn, orgId, api]);

  const fetchStats = useCallback(async () => {
    if (!isLoaded || !isSignedIn || !orgId) return;
    try {
      const data = await runDeduplicated(CACHE_KEY_STATS, () =>
        fetchStatsApi(api),
      );
      if (data != null) setStats(data);
    } catch {
      // Stats errors already logged in api; don't set global error
    }
  }, [isLoaded, isSignedIn, orgId, api]);

  const updateOrganization = useCallback(
    async (updates: Partial<OrganizationData>) => {
      try {
        const organizationData = await updateOrganizationApi(api, updates);
        setOrganization(organizationData);
        return { success: true, data: organizationData };
      } catch (err) {
        const error = err instanceof Error ? err.message : "Unknown error";
        setError(error);
        return { success: false, error };
      }
    },
    [api],
  );

  const updateSettings = useCallback(
    async (settings: Partial<OrganizationSettings>) => {
      try {
        const { settings: updatedSettings, updated_at } =
          await updateSettingsApi(api, settings);
        if (organization) {
          const mergedSettings = {
            ...organization.settings,
            ...updatedSettings,
          } as OrganizationSettings;
          setOrganization({
            ...organization,
            settings: mergedSettings,
            updated_at: updated_at ?? organization.updated_at,
          });
        }
        return { success: true, data: updatedSettings };
      } catch (err) {
        const error = err instanceof Error ? err.message : "Unknown error";
        setError(error);
        logger.error("Failed to update organization settings", {
          error,
          settings,
        });
        return { success: false, error };
      }
    },
    [organization, api],
  );

  const validateSlug = useCallback(
    async (slug: string) => {
      try {
        const { available } = await validateSlugApi(api, slug);
        return { success: true, available };
      } catch (err) {
        const error = err instanceof Error ? err.message : "Unknown error";
        return { success: false, error };
      }
    },
    [api],
  );

  const uploadLogo = useCallback(
    async (file: File) => {
      try {
        const { url } = await uploadLogoApi(file, getToken);
        await fetchOrganization();
        return { success: true, url };
      } catch (err) {
        const error = err instanceof Error ? err.message : "Unknown error";
        setError(error);
        return { success: false, error };
      }
    },
    [fetchOrganization, getToken],
  );

  useEffect(() => {
    const currentAuthState = {
      isLoaded,
      isSignedIn: isSignedIn ?? false,
      orgId: orgId ?? null,
    };
    const lastAuthState = lastAuthStateRef.current;

    if (isLoaded && !isSignedIn) {
      if (lastAuthState?.isSignedIn !== false) {
        setOrganization(null);
        setStats(null);
        setError(null);
        hasFetchedRef.current = false;
      }
      lastAuthStateRef.current = currentAuthState;
      return;
    }

    // Don't fetch until an organization is active — prevents 401s on /post-auth
    // before org activation completes
    if (!orgId) {
      lastAuthStateRef.current = currentAuthState;
      return;
    }

    const authStateChanged =
      !lastAuthState ||
      lastAuthState.isLoaded !== isLoaded ||
      lastAuthState.isSignedIn !== isSignedIn ||
      lastAuthState.orgId !== orgId;

    if (
      isLoaded &&
      isSignedIn &&
      (!hasFetchedRef.current || authStateChanged)
    ) {
      hasFetchedRef.current = true;
      fetchOrganization();
      fetchStats();
    }

    lastAuthStateRef.current = currentAuthState;
  }, [isLoaded, isSignedIn, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    organization,
    stats,
    isLoading,
    error,
    updateOrganization,
    updateSettings,
    validateSlug,
    uploadLogo,
    refreshOrganization: fetchOrganization,
    refreshStats: fetchStats,
  };
}
